<template>
  <div class="groups bg-white h-full">
    <FilterBar @searchChanged="setSearchValue">
      <template v-slot:filter>
        <div>
          <Dropdown
            :options="typeDropdown"
            :selected="typeSelected"
            :title="'Activity Type'"
            v-on:updateOption="updateSelectedType"
            style="min-width: 10rem"
          ></Dropdown>
        </div>
      </template>

      <template v-slot:actions>
        <div>
          <button
            type="button"
            class="action-button hover:opacity-75
             disabled:opacity-50 disabled:cursor-not-allowed"
            @click="addNew()"
              :disabled="shouldShowUpgrade"
          >
            <i class="fas fa-plus"></i>
            Add New
          </button>
        </div>
      </template>
    </FilterBar>

    <div v-if="errored" class="empty">
      <p>
        We are not able to retrieve this information at the moment, please try
        back later
      </p>
    </div>

    <div id="content-area" v-else>
      <div
        style="width: 100%; text-align: center"
        class="spin-holder mt-4"
        v-if="loading"
      >
        <i class="spinner fa fa-spinner fa-spin"></i>
        <p>Loading Groups</p>
      </div>

      <div v-else>
        <div class="w-95p mx-auto pb-10">
          <div
            v-for="item in filteredList"
            :key="item.identifier"
            @click="showGroupDisplay(item)"
            class="rounded-xl bg-gray-100 flex flex-col sm:flex-row mt-8 sm:w-95p ml-8 sm:ml-0 cursor-pointer hover:opacity-50"
          >
            <div class="w-3/12">
              <div
                class="h-full w-full bg-opacity-25 flex items-center content-center justify-center rounded-lg inline-block"
                :class="parseBackgroundColor(item)"
              >
                <div class="text-center text-xl font-bold opacity-50">
                  {{ parseInitials(item.name) }}
                </div>
              </div>
            </div>
            <div class="w-11/12 py-4 sm:ml-8 mt-2 xl:ml-12">
              <h3 class="text-lg xl:text-xl font-bold">{{ item.name }}</h3>
              <h3 class="opacity-80">{{ item.description }}</h3>
              <h3 class="opacity-50 py-2">
                {{ item.pupil_count }} Members
              </h3>
              <div class="flex flex-col sm:flex-row mt-1 justify-between">
                <div class="flex mt-1">
                  <div
                    v-for="pupil in registersSlice(item)"
                    :key="pupil.identifier"
                    class="w-12 h-12 bg-gray-200 -ml-3 rounded-full border border-white inline-block text-center text-xs pt-3 text-gray-600"
                  >
                    {{ parseInitials(pupil.name) }}
                  </div>
                  <div
                    v-if="item.pupil_count > 6"
                    class="w-12 h-12 bg-gray-700 -ml-3 rounded-full border border-white inline-block text-center text-base pt-2 font-semibold text-white"
                  >
                    {{ getAdditionalCount(item) }}+
                  </div>
                </div>
                <div class="mr-12">
                  <button
                    class="action-button bg-action-blue bg-opacity-50"
                  >
                  View Club
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ClubService } from "@/services/club.service";
import { eventTypes } from "@/assets/js/utils";
import { upgradeMixin } from "@/mixins/upgradeMixin.js";

const Dropdown = () => import("@/components/Dropdown.vue");
const FilterBar = () => import("@/components/FilterBarNavigation.vue");

export default {
  name: "videos",
      mixins: [upgradeMixin],

  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
    }),
    componentLoaded() {
      return this.crews.length > 0;
    },
    typeDropdown() {
      let results = [{ id: 0, name: "All Program Types" }].concat(this.types);
      if (this.typeSelected.id === undefined) {
        this.typeSelected = results[0];
      }
      return results;
    },
    registersSlice() {
      return (club) => {
        // targetDate is the argument passed to the handler
        return club.registers != undefined ? club.registers.slice(0, 6) : [];
      };
    },
    filteredList() {
      var vm = this;
      return this.clubs.filter((item) => {
        let filtered = true;

        if (this.typeSelected.id > 0) {
          if (this.typeSelected.value === "education") {
            filtered = ["physical", "education"].includes(item.program_type);
          } else {
            filtered = item.program_type === this.typeSelected.value;
          }
        }

        if (filtered) {
          if (item.title != null) {
            filtered = item.title
              .toLowerCase()
              .includes(vm.search.toLowerCase());
          }
        }

        return filtered;
      });
    },
  },
  data() {
    return {
      loading: true,
      errored: false,

      clubs: [],

      search: "",

      types: eventTypes(),
      typeSelected: {},
    };
  },
  created() {
    this.fetchData();
  },
  mounted() {},
  methods: {
    fetchData() {
      this.loading = true;

      ClubService.getAll()
        .then((response) => (this.clubs = response.data.data))
        .catch((err) => {
          console.log("clubs " + err);
          this.errored = true;
        })
        .finally(() => (this.loading = false));
    },
    showGroupDisplay(club) {
      this.$router.push({
        name: "ClubView",
        params: { club: club, newAddition: false },
      });
    },
    setSearchValue(value) {
      this.search = value;
    },
    updateSelectedType(type) {
      this.typeSelected = type;
    },
    parseInitials(string) {
      return string
        .split(" ")
        .map((n) => n[0])
        .join("")
        .substring(0, 3)
        .toUpperCase();
    },
    getMembersCount(club) {
      return club.registers != undefined ? club.registers.length : 0;
    },
    getAdditionalCount(club) {
      return club.registers != undefined ? club.registers.length - 6 : 0;
    },
    getProgramType(club) {
      let type = this.types.find((t) => t.value === club.program_type);
      return type != undefined ? type.value : "Unknown";
    },
    parseBackgroundColor(club) {
      let type = this.types.find((t) => t.value === club.program_type);
      return type != undefined ? type.class : "bg-indigo-200";
    },
    addNew() {
      let club = {};
      this.$router.push({
        name: "ClubView",
        params: { club: club, newAddition: true },
      });
    },
  },
  components: {
    Dropdown,
    FilterBar,
  },
};
</script>

<style>
</style>
